import React, { useContext, useEffect, useMemo, useState } from "react";
import { SelectUserGroupsDialog } from "../../Dashboard/Tasks/SelectUserGroupsDialog";
import { Dialog, GridGenerator } from "@actinc/dls/components";
import { AlertContext } from "@actinc/dls/context";
import {
  Magnify as MagnifyIcon,
  OpenInNew as OpenInNewIcon,
} from "@actinc/dls/icons";
import _ from "lodash/fp";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  Paper,
  Tab,
  Typography,
} from "@mui/material";
import { useForm, useFormState } from "react-hook-form";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useClient, useMutation } from "urql";

import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";
import { ProgramsContext } from "../../../programsProvider";
import { UsersContext } from "../../../usersProvider";

import {
  AlertDialog,
  ControlledFormInput,
  ControlledFormSelect,
  DataGrid,
  ExpandMoreIcon,
  IconToolbar,
  PreviewContent,
  StandardsDialog,
} from "../../../components";

import {
  useGetMetadataConfigurationOptions,
  useStore,
} from "../../../services/hooks";
import {
  clearState,
  getState,
  removeState,
  setState,
} from "../../../services/sessionState";
import {
  filterOutSelectedPrograms,
  getOptionsByProgram,
  getProgramOptions,
  getSelectedItemOptionsFromMetadata,
  getUserOptions,
  PREVIEW_TYPES,
  setTitle,
} from "../../../services/utils";

import { CloneDialog } from "../CloneDialog";
import { MoveDialog } from "../MoveDialog";
import { RenameDialog } from "../RenameDialog";

const SearchItems = () => {
  const client = useClient();
  const datagridKey = "itemSearch";
  const alertContext = useContext(AlertContext);
  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const programs = useContext(ProgramsContext);
  const users = useContext(UsersContext);
  const [createExternalReviewTaskResponse, createExternalReviewTask] =
    useMutation(createExternalReviewTaskMutation);

  const { control, getValues, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues,
  });
  const { isSubmitting } = useFormState({ control });
  const selectedProgram = watch("program");

  const location = useLocation();
  setState("lastSearchLocation")(location.pathname);
  const navigate = useNavigate();

  const [dialogState, setDialogState] = useState({
    dialogName: "",
    open: false,
  });
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedTab, setSelectedTab] = useState("Metadata");
  const [standardsDialogProps, setStandardsDialogProps] = useState({
    open: false,
  });
  const [selectUserDialogProps, setSelectUserDialogProps] = useState({
    open: false,
  });

  const [search, searchStore] = useStore("search");

  const [
    contentArea,
    developmentState,
    format,
    gradeLevel,
    type,
    elementStatus,
    language,
    release,
    restriction,
    unavailability,
  ] = useGetMetadataConfigurationOptions(
    metadataConfiguration,
    programs,
    "item",
    [
      "contentArea",
      "developmentState",
      "format",
      "gradeLevel",
      "type",
      "elementStatus",
      "language",
      "release",
      "restriction",
      "unavailability",
    ],
  );

  // Set the document title
  useEffect(() => {
    setTitle(["CDE", "Item Search"]);
  }, []);

  useEffect(() => {
    if (!searchStore.client) searchStore.init(client);
  }, [client]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!loading) {
      if (
        (!location.state?.from ||
          [location.pathname, "/workspace"].includes(location.state?.from)) &&
        search?.type === "item"
      ) {
        Object.keys(search.params).map((key) =>
          setValue(key, search.params[key]),
        );
      } else if (!_.isEmpty(search)) {
        searchStore.set({});
      } else if (getState("itemSearchFilters")) {
        const itemSearchFilters = getState("itemSearchFilters");
        if (itemSearchFilters.itemList) {
          changeTab(null, "List");
          Object.keys(itemSearchFilters).map((key) =>
            setValue(key, itemSearchFilters[key]),
          );
          onListSearchSubmit();
        } else {
          changeTab(null, "Metadata");
          Object.keys(itemSearchFilters).map((key) =>
            setValue(key, itemSearchFilters[key]),
          );
          onMetadataSearchSubmit();
        }
        clearState();
      }
    }
    setLoading(false);
  }, [search]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const selectedItemProgramIds = selectedRows.map((item) => item?.program);

  const getFieldOptions = useMemo(() => {
    const selectedRowPrograms = selectedRows.map(({ program }) => program);
    return getSelectedItemOptionsFromMetadata(programs)(selectedRowPrograms)({
      developmentState,
      elementStatus,
      language,
      release,
      restriction,
      unavailability,
    });
  }, [
    developmentState,
    elementStatus,
    language,
    programs,
    release,
    restriction,
    selectedItemProgramIds,
    unavailability,
  ]);

  const permittedActions = useMemo(
    () =>
      selectedRows
        .map((row) => row._actions)
        .reduce(
          (acc, actions) => _.intersection(acc)(actions),
          ["clone", "delete", "move", "createAdditionalTasks"],
        )
        .concat("preview"),
    [selectedRows],
  );

  const programOptions = useMemo(
    () => getProgramOptions(programs)("searchContent"),
    [programs],
  );

  const rows =
    (search?.response?.data &&
      search?.response?.data[`itemsBy${selectedTab}`]) ||
    [];

  const developmentStateOptions = useMemo(() => {
    const devStateValues = [...new Set(rows.map((row) => row.developmentState))]
      .filter(Boolean)
      .sort();
    return devStateValues;
  }, [rows]);

  if (!programOptions.length) return null;

  const changeTab = (_e, tab) => {
    setSelectedTab(tab);
    reset({ ...defaultValues });
    searchStore.set({});
  };

  const closeDialog = () => {
    setDialogState({ open: false, dialogName: "" });
  };

  const deleteItem = async (val) => {
    closeDialog();

    // return if decision is to Cancel
    if (!val) return;

    try {
      setLoading(true);
      const failedIds = await searchStore.delete("item")(deleteItemsMutation)({
        ids: selectedRows.map((item) => item?.id),
        names: selectedRows.map((item) => item?.name),
        objectType: "Item",
        queryType: `itemsBy${selectedTab}`,
      });
      if (failedIds.length > 0) {
        const failedItemNames = selectedRows
          .filter((item) => failedIds.includes(item?.id))
          .map((item) => item?.name);

        setDialogState({
          confirmButtonText: "OK",
          open: true,
          dialogName: "Display ItemNames",
          onClose: () => {
            closeDialog();
          },
          message: (
            <Typography variant="subtitle1">
              {failedItemNames.length > 1
                ? `The following items could not be deleted because they are on a RIMS form:`
                : `The following item could not be deleted because it is on a RIMS form:`}
              {failedItemNames.map((name) => (
                <Typography key={name} variant="body2">
                  {name}
                </Typography>
              ))}
            </Typography>
          ),
        });
      } else {
        alertContext.actions.addAlert({
          message: `Item${
            selectedRows.length > 1 ? "s" : ""
          } successfully deleted`,
          options: { variant: "success" },
        });
      }
    } catch (err) {
      alertContext.actions.addAlert({
        message: `Delete failed!`,
        options: { persist: true, variant: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  const getOriginalQuery = (string) => {
    switch (string) {
      case "searchItemsByList":
        return searchItemsByListQuery;
      case "searchItemsByMetadata":
        return searchItemsByMetadataQuery;
      default:
        break;
    }
  };

  const handleSelectionModelChange = (data) => (selection) => {
    const selectedRows = data.filter(({ id }) => selection.includes(id));
    setSelectedRows(selectedRows);
  };

  const handleRefetch = async () => {
    const originalQueryName =
      search?.response?.operation?.query?.definitions[0]?.name?.value;
    setLoading(true);
    await searchStore.fetch("item")(getOriginalQuery(originalQueryName))({
      ...search?.params,
      requestPolicy: "network-only",
    });
  };

  const onListSearchSubmit = async () => {
    const itemList = [
      ...new Set(
        (Array.isArray(getValues("itemList"))
          ? getValues("itemList").join(",")
          : getValues("itemList") || ""
        )
          .split(/[\n,]/)
          .map((name) => name.trim())
          .filter(Boolean),
      ),
    ];
    if (itemList.length) {
      setLoading(true);
      await searchStore.fetch("item")(searchItemsByListQuery)({ itemList });
      const results = searchStore
        .get()
        .response.data.itemsByList.map(({ name }) => name.toLowerCase());
      const itemsNotFound = itemList.filter(
        (name) => !results.includes(name.toLowerCase()),
      );
      if (itemsNotFound.length) {
        setDialogState({ open: true, itemsNotFound, dialogName: "listSearch" });
      }
    }
  };

  const onMetadataSearchSubmit = async () => {
    const searchValues = getValues();
    setLoading(true);
    await searchStore.fetch("item")(searchItemsByMetadataQuery)({
      ...searchValues,
      name: searchValues?.name.trim(),
      passageName: searchValues?.passageName.trim(),
    });
  };

  const openDeleteDialog = () => {
    setDialogState({
      confirmButtonText: "Delete",
      dialogName: "delete",
      message: (
        <Typography variant="subtitle1">
          {selectedRows.length > 1 ? (
            <>
              {`These ${selectedRows.length} items will be permanently deleted`}
              {selectedRows.map(({ id, name }) => (
                <Typography key={id} sx={{ paddingLeft: 2 }} variant="body2">
                  {name}
                </Typography>
              ))}
            </>
          ) : (
            `Item ${selectedRows[0]?.name} will be permanently deleted`
          )}
        </Typography>
      ),
      onClose: deleteItem,
      open: true,
      title: `Confirm Item${selectedRows.length > 1 ? "s" : ""} Deletion`,
    });
  };

  const openItem = (newWindow) => (row) => () => {
    if (newWindow) {
      window.open(row.workspace.uri, row.workspace.name);
    } else {
      navigate(row.workspace.uri, { reloadDocument: true });
    }
  };

  const openCloneItemDialog = () => {
    const contentSpecialistOptions = getUserOptions(users)({
      activeOnly: true,
      programs: selectedItemProgramIds,
      roles: ["Content Specialist"],
    });
    const itemWriterOptions = getUserOptions(users)({
      activeOnly: true,
      programs: selectedItemProgramIds,
      roles: ["Item Writer"],
    });
    setDialogState({
      dialogName: "clone",
      fieldOptions: {
        contentSpecialistOptions,
        itemWriterOptions,
        ...getFieldOptions,
        developmentState: [
          "Content Review 2",
          "Content Review 3",
          "Editorial Review",
          "Content Review 4",
          "Accessibility External",
          "Accessibility Internal",
        ].map((s) => ({ label: s, value: s })),
      },
      selectedRows,
      mutation: cloneItemMutation,
      objectType: "item",
      onClose: () => {
        closeDialog();
      },
      open: true,
      program: selectedItemProgramIds[0],
      programs,
    });
  };

  const openMoveItemDialog = () => {
    setDialogState({
      dialogName: "move",
      handleRefetch: () => {
        handleRefetch();
        closeDialog();
      },
      objectType: "item",
      open: true,
      optionsToMove: selectedRows,
      onClose: () => {
        closeDialog();
      },
      programOptions: filterOutSelectedPrograms(programOptions)(selectedRows),
    });
  };

  const openRenameDialog = (selectedRow) => (e) => {
    setDialogState({
      dialogName: "rename",
      id: selectedRow?.id,
      currentName: selectedRow?.name,
      onClose: () => {
        closeDialog();
      },
      objectType: "item",
      open: true,
      handleRefetch,
    });
  };

  const openAssignDialog = ({ users }) => {
    const selectedRowIds = selectedRows.map(({ id }) => id);
    const programs = [
      ...new Set(
        rows
          .filter(({ id }) => selectedRowIds.includes(id))
          .map(({ program }) => program),
      ),
    ];
    const userOptions = getUserOptions(users)({
      programs,
      roles: ["External Reviewer"],
    });

    setSelectUserDialogProps({
      details: ["dueDate", "instructions"],
      onClose: async (details) => {
        setSelectUserDialogProps({ open: false });
        if (!details) return;
        setLoading(true);
        await createExternalReviewTask({
          details: details,
          id: selectedRowIds,
          taskDefinition: "External Review",
          type: "item",
        });
        setLoading(false);
        alertContext.actions.addAlert({
          message: `External Reviewer assignment successfull!`,
          options: { variant: "success" },
        });
      },
      open: true,
      programs,
      roles: ["External Reviewer"],
      users: userOptions,
    });
  };

  const openStandardsDialog = () => {
    setStandardsDialogProps({
      action: "search",
      defaultValues: { standards: getValues("standards") },
      onClose: (standards = []) => {
        setValue("standards", standards);
        setStandardsDialogProps({ open: false });
      },
      open: true,
      selectedStandards: [],
    });
  };

  const columns = [
    {
      field: "name",
      flex: 1,
      headerName: "Item Name",
      renderCell: (params) => {
        return (
          <>
            <Link
              component={RouterLink}
              disabled={!params?.row._actions.includes("view")}
              onClick={() => setState("itemSearchFilters")(getValues())}
              reloadDocument
              to={params?.row.workspace.uri}
              underline="always"
            >
              {params.value}
            </Link>
            <IconButton
              color="secondary"
              component={RouterLink}
              disabled={!params?.row._actions.includes("view")}
              onClick={() => setState("itemSearchFilters")(getValues())}
              size="small"
              target={params?.row.workspace.name}
              to={params?.row.workspace.uri}
            >
              <OpenInNewIcon />
            </IconButton>
            <PreviewContent
              objects={[{ id: params?.row.id }]}
              objectType="Item"
              previewTypes={PREVIEW_TYPES}
            />
          </>
        );
      },
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
    },
    {
      field: "passageName",
      flex: 1,
      headerName: "Passage Name",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      valueGetter: getPassageName,
    },
    {
      field: "programName",
      headerName: "Program",
      minWidth: 200,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "singleSelect",
      valueGetter: getProgramName(programs),
      valueOptions: programOptions.map(({ label }) => label),
    },
    {
      field: "description",
      flex: 1,
      headerName: "Description",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      valueGetter: getNonNullValue("description"),
    },
    {
      field: "contentSpecialist",
      headerName: "Content Specialist",
      minWidth: 200,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      valueGetter: getContentSpecialistName(users),
    },
    {
      field: "developmentState",
      headerName: "Development State",
      minWidth: 250,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "singleSelect",
      valueGetter: getNonNullValue("developmentState"),
      valueOptions: developmentStateOptions,
    },
    {
      field: "gradeLevel",
      headerName: "Grade",
      minWidth: 110,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      valueGetter: getNonNullValue("gradeLevel"),
    },
  ];

  const dataGridProps = {
    actions: [
      {
        disabled: (row) => !row?._actions?.includes("view"),
        label: "Open",
        onClick: openItem(false),
      },
      {
        disabled: (row) => !row?._actions?.includes("view"),
        label: "Open in New Window",
        onClick: openItem(true),
      },
      {
        disabled: (row) => !row?._actions?.includes("rename"),
        label: "Rename",
        onClick: openRenameDialog,
      },
    ],
    checkboxSelection: rows
      .map((item) => item?.[`_actions`])
      .flat()
      .some((action) => ["delete", "move", "clone"].includes(action)),
    columns,
    disableRowSelectionOnClick: true,
    error: search?.response?.error?.message,
    isRowSelectable: ({ row }) => {
      return row?.["_actions"].some((action) =>
        ["delete", "move", "clone"].includes(action),
      );
    },
    loading,
    onRowSelectionModelChange: handleSelectionModelChange(rows),
    rows,
    datagridKey,
    sortModel:
      selectedTab === "Metadata"
        ? [
            {
              field: "name",
              sort: "asc",
            },
          ]
        : [],
  };

  const dialogProps = {
    draggable: false,
    fullWidth: true,
    maxWidth: "xs",
    onClose: closeDialog,
    open: dialogState.open && dialogState.dialogName === "listSearch",
    scroll: "paper",
    title: "Items Not Found",
  };

  const rowLimitOptions = [100, 500, 1000].map((value) => ({
    label: value,
    value,
  }));

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: 2,
        }}
      >
        <Typography variant="h4">Items</Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <TabContext value={selectedTab}>
          <TabList onChange={changeTab}>
            <Tab key="Metadata" label="Metadata" value="Metadata" />
            <Tab key="List" label="List" value="List" />
          </TabList>
          <TabPanel
            key="Metadata"
            index="0"
            sx={{ overflowY: "auto" }}
            value="Metadata"
          >
            <Paper
              elevation={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                margin: 2,
                padding: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <form onSubmit={handleSubmit(onMetadataSearchSubmit)}>
                  <Box
                    sx={{
                      alignItems: "end",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <GridGenerator
                      groups={[
                        [
                          {
                            children: (
                              <ControlledFormSelect
                                control={control}
                                fullWidth={true}
                                id={`itemSearch-program`}
                                label="Program"
                                name="program"
                                options={programOptions}
                              />
                            ),
                            key: `itemSearch-program`,
                          },
                          {
                            children: (
                              <ControlledFormInput
                                control={control}
                                fullWidth={true}
                                id={`itemSearch-name`}
                                label="Item Name"
                                name="name"
                              />
                            ),
                            key: `itemSearch-name`,
                          },
                          {
                            children: (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  justifyContent: "space-between",
                                }}
                              >
                                <ControlledFormSelect
                                  control={control}
                                  id={`itemSearch-rowLimit`}
                                  label="Number of Results"
                                  name="rowLimit"
                                  options={rowLimitOptions}
                                />
                                <Box sx={{ marginBottom: 2 }}>
                                  <ExpandMoreIcon
                                    expand={expanded}
                                    onClick={() => setExpanded(!expanded)}
                                  />
                                </Box>
                              </Box>
                            ),
                            key: `itemSearch-rowLimit`,
                          },
                        ],
                      ]}
                    />
                    <Collapse
                      aria-label={`collapsable panel ${
                        expanded ? "expanded" : "collapsed"
                      }`}
                      in={expanded}
                      sx={{ width: "100%" }}
                      timeout="auto"
                    >
                      <GridGenerator
                        groups={[
                          [
                            {
                              children: (
                                <ControlledFormInput
                                  control={control}
                                  fullWidth={true}
                                  id={`itemSearch-passageName`}
                                  label="Passage Name"
                                  name="passageName"
                                />
                              ),
                              key: `itemSearch-passageName`,
                            },
                            {
                              children: (
                                <ControlledFormInput
                                  control={control}
                                  fullWidth={true}
                                  id={`itemSearch-description`}
                                  label="Description"
                                  name="description"
                                />
                              ),
                              key: `itemSearch-description`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`itemSearch-contentSpecialist`}
                                  label="Content Specialist"
                                  name="contentSpecialist"
                                  options={getUserOptions(users)({
                                    activeOnly: false,
                                    programs: [selectedProgram],
                                    roles: ["Content Specialist"],
                                  })}
                                />
                              ),
                              key: `itemSearch-contentSpecialist`,
                            },
                          ],
                          [
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`itemSearch-itemWriter`}
                                  label="Item Writer"
                                  name="itemWriter"
                                  options={getUserOptions(users)({
                                    activeOnly: false,
                                    programs: [selectedProgram],
                                    roles: ["Item Writer"],
                                  })}
                                />
                              ),
                              key: `itemSearch-itemWriter`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`itemSearch-format`}
                                  label="Item Format"
                                  name="format"
                                  options={getOptionsByProgram(programs)([
                                    selectedProgram,
                                  ])(format)}
                                />
                              ),
                              key: `itemSearch-format`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  fullWidth={true}
                                  disabled={!selectedProgram}
                                  id={`itemSearch-developmentState`}
                                  label="Development State"
                                  name="developmentState"
                                  options={getOptionsByProgram(programs)([
                                    selectedProgram,
                                  ])(developmentState)}
                                />
                              ),
                              key: `itemSearch-developmentState`,
                            },
                          ],
                          [
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`itemSearch-gradeLevel`}
                                  label="Grade Level"
                                  name="gradeLevel"
                                  options={getOptionsByProgram(programs)([
                                    selectedProgram,
                                  ])(gradeLevel)}
                                />
                              ),
                              key: `itemSearch-gradeLevel`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`itemSearch-contentArea`}
                                  label="Content Area"
                                  name="contentArea"
                                  options={getOptionsByProgram(programs)([
                                    selectedProgram,
                                  ])(contentArea)}
                                />
                              ),
                              key: `itemSearch-contentArea`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`itemSearch-type`}
                                  label="Item Type"
                                  name="type"
                                  options={getOptionsByProgram(programs)([
                                    selectedProgram,
                                  ])(type)}
                                />
                              ),
                              key: `itemSearch-type`,
                            },
                          ],
                          [
                            {
                              children: (
                                <ControlledFormInput
                                  control={control}
                                  endAdornment={
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="open standards dialog"
                                        edge="end"
                                        onClick={openStandardsDialog}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        <MagnifyIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  }
                                  fullWidth={true}
                                  id={`itemSearch-standards`}
                                  label="Standards"
                                  name="standards"
                                />
                              ),
                              key: `itemSearch-standards`,
                            },
                            {
                              children: <Box />,
                              key: `itemSearch-blank1`,
                            },
                            {
                              children: <Box />,
                              key: `itemSearch-blank2`,
                            },
                          ],
                        ]}
                      />
                    </Collapse>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Button
                        onClick={() => {
                          reset({ ...defaultValues });
                          searchStore.set({});
                          removeState("itemSearchFilters");
                          setSelectedRows([]);
                        }}
                        sx={{ marginRight: 2 }}
                        variant="outlined"
                      >
                        Reset
                      </Button>
                      <Button
                        disabled={isSubmitting}
                        sx={{ marginRight: 2 }}
                        type="submit"
                        variant="contained"
                      >
                        Search
                      </Button>
                    </Box>
                  </Box>
                </form>
              </Box>
            </Paper>
          </TabPanel>
          <TabPanel
            key="List"
            index="1"
            sx={{ overflowY: "auto" }}
            value="List"
          >
            <Paper
              elevation={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                margin: 2,
                padding: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <form onSubmit={handleSubmit(onListSearchSubmit)}>
                  <Box
                    sx={{
                      alignItems: "end",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <GridGenerator
                      groups={[
                        [
                          {
                            children: (
                              <ControlledFormInput
                                control={control}
                                fullWidth={true}
                                inputProps={{
                                  maxRows: 10,
                                  minRows: 10,
                                }}
                                label="Item Names"
                                multiline={true}
                                name="itemList"
                              />
                            ),
                            key: `itemSearch-list`,
                          },
                        ],
                      ]}
                    />
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Button
                        onClick={() => {
                          reset({ ...defaultValues });
                          searchStore.set({});
                          setValue("itemList", "");
                        }}
                        sx={{ marginRight: 2 }}
                        variant="outlined"
                      >
                        Reset
                      </Button>
                      <Button
                        disabled={isSubmitting}
                        sx={{ marginRight: 2 }}
                        type="submit"
                        variant="contained"
                      >
                        Search
                      </Button>
                    </Box>
                  </Box>
                </form>
              </Box>
            </Paper>
          </TabPanel>
        </TabContext>
      </Box>
      {(search?.response || loading) && (
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            margin: 1,
          }}
        >
          {!!selectedRows.length && (
            <IconToolbar
              handleClone={openCloneItemDialog}
              handleDelete={openDeleteDialog}
              handleMove={openMoveItemDialog}
              handleAssignUser={() =>
                openAssignDialog({
                  users: users,
                })
              }
              objectType="Item"
              permittedActions={permittedActions}
              selectedRows={selectedRows}
            ></IconToolbar>
          )}
          <Box
            sx={{
              flexGrow: 1,
              minHeight: "500px",
            }}
          >
            <DataGrid {...dataGridProps}></DataGrid>
          </Box>
        </Paper>
      )}
      <Dialog {...dialogProps}>
        <DialogContent>
          <Typography variant="subtitle2">
            No results matched the following item
            {!!dialogState?.itemsNotFound &&
            dialogState.itemsNotFound.length > 1
              ? "s"
              : ""}
            :
          </Typography>
          <List dense={true} disablePadding={true}>
            {!!dialogState?.itemsNotFound &&
              dialogState.itemsNotFound.map((itemName) => (
                <ListItem key={itemName}>{itemName}</ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} variant="text">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {dialogState.open && dialogState.dialogName === "delete" && (
        <AlertDialog {...dialogState} />
      )}
      {dialogState.open && dialogState.dialogName === "clone" && (
        <CloneDialog {...dialogState} />
      )}
      {dialogState.open && dialogState.dialogName === "move" && (
        <MoveDialog {...dialogState} />
      )}
      {dialogState.open && dialogState.dialogName === "rename" && (
        <RenameDialog {...dialogState} />
      )}
      {standardsDialogProps.open && (
        <StandardsDialog {...standardsDialogProps} />
      )}
      {dialogState.open && dialogState.dialogName === "Display ItemNames" && (
        <AlertDialog {...dialogState} />
      )}
      {selectUserDialogProps.open && (
        <SelectUserGroupsDialog {...selectUserDialogProps} />
      )}
    </>
  );
};

const cloneItemMutation = `
  mutation CloneItems($itemsToClone: [ID!]!, $data: ItemInput!, $cloneRelationships: Boolean!, $relationshipTypeToClone: RelationshipTypeEnum, $ttsGenerate: Boolean! ) {
    cloneItems(itemsToClone: $itemsToClone, data: $data, cloneRelationships: $cloneRelationships, relationshipTypeToClone: $relationshipTypeToClone, ttsGenerate: $ttsGenerate) {
      contentArea
      contentSpecialist {
        id
      }
      developmentState
      id
      itemWriter {
        id
      }
      name
      program
      guid
    }
  }
`;

const deleteItemsMutation = `
    mutation DeleteItems($ids: [ID!]!, $objectType: ObjectTypeEnum!) {
      deleteItems(ids: $ids, objectType: $objectType) {
        id
      }
    }
  `;

const searchItemsByMetadataQuery = `
  query searchItemsByMetadata($contentArea: String, $contentSpecialist: String, $description: String, $developmentState: String, $format: String, $gradeLevel: String, $itemWriter: String, $name: String, $passageName: String, $program: ID, $rowLimit: Int, $standards: [String!], $type: String) {
    itemsByMetadata(contentArea: $contentArea, contentSpecialist: $contentSpecialist, description: $description, developmentState: $developmentState, format: $format, gradeLevel: $gradeLevel, itemWriter: $itemWriter, name: $name, passageName: $passageName, program: $program, rowLimit: $rowLimit, standards: $standards type: $type) {
      id
      contentSpecialist {
        id
        name
      }
      description
      developmentState
      gradeLevel
      name
      passage {
        id
        name
      }
      program
      versionID
      _actions
    }
  }`;

const searchItemsByListQuery = `
  query searchItemsByList($itemList: [String!]!, $program: ID) {
    itemsByList(itemList: $itemList, program: $program) {
      id
      contentSpecialist {
        id
        name
      }
      description
      developmentState
      gradeLevel
      name
      passage {
        id
        name
      }
      program
      versionID
      _actions
    }
  }`;

const createExternalReviewTaskMutation = `
  mutation CreateExternalReviewTask($details: [TaskCompletionDetails!], $id: [ID!]!, $taskDefinition: String!, $type: String!) {
    createAdditionalTask(details: $details,id: $id,taskDefinition: $taskDefinition, type: $type) {
      id
    }
  }`;

const defaultValues = {
  contentArea: null,
  contentSpecialist: null,
  description: "",
  developmentState: null,
  format: null,
  gradeLevel: null,
  itemList: null,
  itemWriter: null,
  name: "",
  passageName: "",
  program: null,
  rowLimit: 100,
  standards: [],
  type: null,
};

const getNonNullValue = (fieldName) => (params) =>
  `${params.row?.[fieldName] || ""}`;
const getPassageName = (params) => `${params.row?.passage?.name || ""}`;
const getProgramName = (programs) => (params) =>
  `${programs.find(({ id }) => id === params.row?.program)?.name || ""}`;
const getContentSpecialistName = (users) => (params) =>
  `${
    users.find(({ id }) => id === params.row?.contentSpecialist?.id)?.name || ""
  }`;

export {
  SearchItems,
  searchItemsByMetadataQuery,
  searchItemsByListQuery,
  createExternalReviewTaskMutation,
  getProgramName,
};
